/**
 * F2.f.b + C.3 - Consolidated admin progression page.
 *
 * 5-tab shell:
 *   1. Users     - progression-focused directory (reuses /api/admin/users?enrich=true)
 *   2. Rules     - point_rules editor (GET/PATCH /api/admin/progression/config)
 *   3. Ranks     - 10 ranks + 30 thresholds editor (same config endpoint)
 *   4. Achievements - CRUD via /api/admin/achievements (F2.d.3)
 *   5. Ops       - Ledger + Audit + Drift widgets (existing, relocated)
 *
 * Ember/navy palette, no em/en-dashes, shadcn primitives.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertTriangle, ChevronDown, ChevronRight, Users as UsersIcon,
  Sliders, Award, Trophy, Activity, Plus, Edit2, Trash2, ArrowUp,
  ArrowDown, RefreshCw,
} from 'lucide-react';
import api from '@/utils/api';

// ---------------------------------------------------------------------------
// Drift Alerts stat card - simple readout of last-24h drift row count.
// ---------------------------------------------------------------------------
const DriftAlertsCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/progression/drift-summary')
      .then(r => setData(r.data))
      .catch(() => setData({ drift_count_24h: 0 }));
  }, []);
  const n = data?.drift_count_24h ?? 0;
  const accent = n > 0 ? 'border-amber-400 bg-amber-50/60' : 'border-primary-100 bg-white';
  const text = n > 0 ? 'text-amber-800' : 'text-neutral-500';
  return (
    <Card data-testid="admin-drift-alerts-card" className={`border ${accent}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <AlertTriangle className={`h-5 w-5 ${n > 0 ? 'text-amber-600' : 'text-neutral-400'}`} />
        <div>
          <div className={`text-[10px] uppercase tracking-widest ${text}`}>
            Drift alerts (24h)
          </div>
          <div className={`font-display text-2xl font-bold ${n > 0 ? 'text-amber-900' : 'text-neutral-700'}`}>
            {n}
          </div>
          {data?.last_detected_at && (
            <div className="text-[11px] text-neutral-500 mt-0.5">
              Last · {new Date(data.last_detected_at).toLocaleString()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Admin Ledger Viewer - filters + table + expand-row + cursor paginate.
// ---------------------------------------------------------------------------
const KIND_OPTIONS = [
  'event_checkin', 'rsvp_submitted', 'rsvp_confirmed', 'event_hosted',
  'survey_completed', 'assessment_added', 'full_result_imported',
  'profile_section_complete', 'profile_photo_upload',
  'achievement_bonus', 'admin_manual_adjust',
];
const SOURCE_OPTIONS = ['system', 'auto', 'admin_manual', 'backfill', 'admin_manual_grant'];

const AdminLedgerViewer = () => {
  const [filters, setFilters] = useState({ user_id: '', kind: '', source: '', since: '', until: '', limit: 50 });
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursors, setPrevCursors] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchRows = useCallback(async (cursor) => {
    setLoading(true);
    try {
      const params = { limit: filters.limit };
      Object.entries(filters).forEach(([k, v]) => {
        if (v && k !== 'limit') params[k] = v;
      });
      if (cursor) params.cursor = cursor;
      const r = await api.get('/admin/progression/ledger', { params });
      setRows(r.data.rows || []);
      setNextCursor(r.data.next_cursor || null);
      if (r.data.total_count != null) setTotal(r.data.total_count);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchRows(null); }, [fetchRows]);

  const onNext = () => {
    if (!nextCursor) return;
    setPrevCursors((p) => [...p, nextCursor]);
    fetchRows(nextCursor);
  };
  const onPrev = () => {
    const stack = [...prevCursors];
    stack.pop();
    setPrevCursors(stack);
    fetchRows(stack[stack.length - 1] || null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-lg text-primary-900">Progression Ledger</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Input
            data-testid="admin-ledger-filter-user_id"
            placeholder="user_id"
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
          />
          <Select
            value={filters.kind || 'any'}
            onValueChange={(v) => setFilters({ ...filters, kind: v === 'any' ? '' : v })}
          >
            <SelectTrigger data-testid="admin-ledger-filter-kind"><SelectValue placeholder="kind" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any kind</SelectItem>
              {KIND_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.source || 'any'}
            onValueChange={(v) => setFilters({ ...filters, source: v === 'any' ? '' : v })}
          >
            <SelectTrigger data-testid="admin-ledger-filter-source"><SelectValue placeholder="source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any source</SelectItem>
              {SOURCE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            data-testid="admin-ledger-filter-since"
            type="date"
            value={filters.since}
            onChange={(e) => setFilters({ ...filters, since: e.target.value })}
          />
          <Input
            data-testid="admin-ledger-filter-until"
            type="date"
            value={filters.until}
            onChange={(e) => setFilters({ ...filters, until: e.target.value })}
          />
          <Input
            data-testid="admin-ledger-filter-limit"
            type="number"
            min="1"
            max="500"
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: Math.min(500, parseInt(e.target.value) || 50) })}
          />
        </div>
        {total != null && (
          <div className="text-xs text-neutral-500">Total (unfiltered by cursor): {total}</div>
        )}
        <div className="border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50">
              <tr className="text-left text-xs uppercase tracking-widest text-neutral-500">
                <th className="p-2 w-6"></th>
                <th className="p-2">When</th>
                <th className="p-2">User</th>
                <th className="p-2">Kind</th>
                <th className="p-2 text-right">Points</th>
                <th className="p-2">Source</th>
                <th className="p-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="7" className="p-6 text-center text-neutral-500">No rows.</td></tr>
              )}
              {rows.map((r) => {
                const positive = (r.points || 0) >= 0;
                const isOpen = !!expanded[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      data-testid={`admin-ledger-row-${r.id}`}
                      onClick={() => setExpanded({ ...expanded, [r.id]: !isOpen })}
                      className="border-t hover:bg-neutral-50 cursor-pointer"
                    >
                      <td className="p-2">{isOpen ? <ChevronDown className="h-3 w-3"/> : <ChevronRight className="h-3 w-3"/>}</td>
                      <td className="p-2 text-xs text-neutral-600">{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                      <td className="p-2 text-xs font-mono truncate max-w-[140px]">{r.user_id}</td>
                      <td className="p-2 text-xs">{r.kind}</td>
                      <td className={`p-2 text-right font-semibold ${positive ? 'text-success-700' : 'text-red-700'}`}>
                        {positive ? '+' : ''}{r.points}
                      </td>
                      <td className="p-2 text-xs text-neutral-600">{r.source || '-'}</td>
                      <td className="p-2 text-xs truncate max-w-[280px]">{r.description || '-'}</td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`admin-ledger-row-detail-${r.id}`} className="bg-neutral-50">
                        <td colSpan="7" className="p-3">
                          <pre className="text-[10px] whitespace-pre-wrap font-mono text-neutral-700">
                            {JSON.stringify(r, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center">
          <Button
            data-testid="admin-ledger-prev"
            variant="outline" size="sm"
            disabled={prevCursors.length === 0}
            onClick={onPrev}
          >
            Previous
          </Button>
          <Button
            data-testid="admin-ledger-next"
            variant="outline" size="sm"
            disabled={!nextCursor}
            onClick={onNext}
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Admin Audit Feed - short-poll every 10s.
// ---------------------------------------------------------------------------
const AdminAuditFeed = () => {
  const [entries, setEntries] = useState([]);
  const [latest, setLatest] = useState(null);

  const poll = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (latest) params.since = latest;
      const r = await api.get('/admin/maintenance/audit-feed', { params });
      const fresh = r.data.entries || [];
      if (fresh.length > 0) {
        setEntries((old) => {
          // C.3 fix: React StrictMode invokes effects twice in dev, so
          // the same audit rows can arrive twice on mount. Dedupe by id
          // before capping to 100 to avoid the 'two children with the
          // same key' warning under React StrictMode.
          const seen = new Set();
          const merged = [];
          for (const row of [...fresh, ...old]) {
            const id = row?.id;
            if (!id || seen.has(id)) continue;
            seen.add(id);
            merged.push(row);
          }
          return merged.slice(0, 100);
        });
        setLatest(fresh[0]?.created_at || latest);
      }
    } catch { /* ignore */ }
  }, [latest]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [poll]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-lg text-primary-900">Admin Audit Feed</CardTitle>
      </CardHeader>
      <CardContent data-testid="admin-audit-feed">
        {entries.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">No recent admin activity.</p>
        ) : (
          <ul className="space-y-2 max-h-[400px] overflow-y-auto">
            {entries.map((e) => (
              <li
                key={e.id}
                data-testid={`admin-audit-entry-${e.id}`}
                className="flex items-start gap-3 p-2 rounded-md bg-neutral-50 border border-primary-100"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-800 text-white flex items-center justify-center text-xs font-semibold">
                  {(e.actor_name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-primary-900">
                    <span className="font-semibold">{e.actor_name}</span>
                    <span className="text-neutral-400"> · </span>
                    <span className="font-mono text-xs text-primary-700">{e.action}</span>
                    <span className="text-neutral-400"> · </span>
                    <span className="text-neutral-700 truncate">{e.target}</span>
                  </div>
                  {e.reason && <div className="text-xs text-neutral-600 italic mt-0.5">{e.reason}</div>}
                  <div className="text-[10px] text-neutral-400 mt-0.5">
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

// ===========================================================================
// C.3 Tab 1 - Users progression browser.
// Reuses /api/admin/users?enrich=true to pull the joined progression shape,
// then filters + sorts to a progression-focused table with a mini side
// panel for manual adjust / recompute / recent ledger.
// ===========================================================================
const RANK_OPTIONS = [
  { v: '', label: 'Any rank' },
  { v: 'Naya Dost', label: 'Naya Dost' },
  { v: 'Aashna Dost', label: 'Aashna Dost' },
  { v: 'Yaar Dost', label: 'Yaar Dost' },
  { v: 'Dil Dost', label: 'Dil Dost' },
  { v: 'Meherbaan Dost', label: 'Meherbaan Dost' },
];

const ProgressionUsersBrowser = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [rank, setRank] = useState('');
  const [activity, setActivity] = useState('any');
  const [selected, setSelected] = useState(null);
  // C.3 review fix: debounce search input so /admin/users isn't hit on
  // every keystroke.
  const [dq, setDq] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const params = {
        enrich: true, limit: 100,
        sort_by: 'points_total',
        q: dq || undefined,
      };
      const { data } = await api.get('/admin/users', { params });
      let rr = data.users;
      if (rank) rr = rr.filter(u => (u.progression?.rank || '') === rank);
      setRows(rr);
      setTotal(data.total_count);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPage();   }, [dq, rank, activity]);

  return (
    <div className="space-y-3" data-testid="admin-progression-users">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / email"
          data-testid="admin-progression-users-search"
        />
        <Select value={rank || 'any'} onValueChange={(v) => setRank(v === 'any' ? '' : v)}>
          <SelectTrigger data-testid="admin-progression-users-rank"><SelectValue placeholder="Any rank" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any rank</SelectItem>
            {RANK_OPTIONS.filter(r => r.v).map(r => (
              <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={activity} onValueChange={setActivity}>
          <SelectTrigger data-testid="admin-progression-users-activity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any activity</SelectItem>
            <SelectItem value="active">Active (30d)</SelectItem>
            <SelectItem value="dormant">Dormant</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-neutral-500 flex items-center justify-end">
          {rows.length} of {total.toLocaleString()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[2fr_320px] gap-4">
        <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <div className="grid grid-cols-[2fr_0.7fr_1fr_0.7fr] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 border-b bg-neutral-50">
            <div>User</div>
            <div className="text-right">Level</div>
            <div>Rank</div>
            <div className="text-right">Points</div>
          </div>
          {rows.length === 0 && !loading && (
            <div className="p-8 text-center text-sm text-neutral-500">No matches.</div>
          )}
          {rows.map((u) => (
            <div
              key={u.id}
              onClick={() => setSelected(u)}
              className={`grid grid-cols-[2fr_0.7fr_1fr_0.7fr] gap-3 px-4 py-2.5 border-b last:border-b-0 items-center cursor-pointer hover:bg-primary-50/30 ${selected?.id === u.id ? 'bg-primary-50/60' : ''}`}
              data-testid={`admin-progression-users-row-${u.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-primary-900 truncate">
                  {(u.first_name || '') + ' ' + (u.last_name || '')}
                </div>
                <div className="text-xs text-neutral-500 truncate">{u.email}</div>
              </div>
              <div className="text-right text-sm font-semibold text-primary-900">
                L{u.progression?.level ?? '-'}
              </div>
              <div className="text-xs text-primary-900 truncate">
                {u.progression?.rank || '-'}
              </div>
              <div className="text-right text-sm font-bold text-primary-900">
                {u.progression ? (u.progression.points_total || 0).toLocaleString() : '-'}
              </div>
            </div>
          ))}
        </div>

        <UserProgressionPanel user={selected} onChange={fetchPage} />
      </div>
    </div>
  );
};

const UserProgressionPanel = ({ user, onChange }) => {
  const [ledger, setLedger] = useState([]);
  const [busy, setBusy] = useState(false);
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!user) { setLedger([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${user.id}/detail`);
        setLedger(data.recent_ledger || []);
      } catch { setLedger([]); }
    })();
  }, [user]);

  if (!user) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-xs text-neutral-500 self-start">
        Click a user to inspect their progression.
      </div>
    );
  }

  const doAdjust = async () => {
    const p = parseInt(points, 10);
    if (!p || p === 0) { alert('Points must be a non-zero integer'); return; }
    if ((reason || '').trim().length < 5) { alert('Reason must be at least 5 characters'); return; }
    if (!window.confirm(`Award ${p > 0 ? '+' : ''}${p} points to ${user.email}?`)) return;
    setBusy(true);
    try {
      await api.post('/admin/progression/adjust', {
        user_id: user.id, points: p, reason: reason.trim(),
      });
      setPoints(''); setReason('');
      onChange && onChange();
      alert('Adjustment applied.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Adjust failed');
    } finally { setBusy(false); }
  };

  const doRecompute = async () => {
    if (!window.confirm(`Force recompute progression for ${user.email}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/progression/recompute/${user.id}`, {});
      onChange && onChange();
      alert('Recompute queued.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Recompute failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3 self-start" data-testid="admin-progression-users-panel">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Selected user</div>
        <div className="text-sm font-semibold text-primary-900 truncate">
          {(user.first_name || '') + ' ' + (user.last_name || '')}
        </div>
        <div className="text-xs text-neutral-500 truncate">{user.email}</div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-neutral-50 p-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Level</div>
          <div className="text-lg font-bold text-primary-900">L{user.progression?.level ?? '-'}</div>
        </div>
        <div className="rounded-lg bg-neutral-50 p-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Rank</div>
          <div className="text-xs font-semibold text-primary-900 truncate">{user.progression?.rank || '-'}</div>
        </div>
        <div className="rounded-lg bg-neutral-50 p-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Points</div>
          <div className="text-lg font-bold text-primary-900">{user.progression ? (user.progression.points_total || 0).toLocaleString() : '-'}</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Manual adjust</div>
        <Input
          type="number"
          placeholder="Points (positive or negative)"
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          data-testid="admin-progression-users-adjust-points"
        />
        <Textarea
          placeholder="Reason (min 5 chars, kept in audit)"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="admin-progression-users-adjust-reason"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={busy}
            className="flex-1"
            onClick={doAdjust}
            data-testid="admin-progression-users-adjust-submit"
          >
            Apply adjust
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={doRecompute}
            data-testid="admin-progression-users-recompute"
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Recompute
          </Button>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Recent ledger</div>
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {ledger.length === 0 && (
            <div className="text-xs text-neutral-500 italic">No entries.</div>
          )}
          {ledger.map((row) => (
            <div key={row.id} className="text-[11px] flex items-center justify-between px-2 py-1 rounded bg-neutral-50">
              <span className="text-primary-900 truncate">{row.reason || row.kind}</span>
              <span className={`font-semibold ${row.points >= 0 ? 'text-success-700' : 'text-red-600'}`}>
                {row.points > 0 ? '+' : ''}{row.points}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// C.3 Tab 2 - Rules editor (POINT_RULES).
// PATCH /api/admin/progression/config enforces slug regex, non-negative int,
// fairness denylist server-side; we mirror the guardrails client-side for UX.
// ===========================================================================
const RULE_SLUG_RE = /^[a-z][a-z0-9_]{2,63}$/;
const FAIRNESS_DENYLIST = ['profile_views', 'photos_liked', 'referrer_visits'];

const RulesEditor = () => {
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/admin/progression/config');
    setConfig(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (kind) => {
    const reason = window.prompt(`Delete rule '${kind}'? Type a reason (min 5 chars):`);
    if (!reason || reason.trim().length < 5) return;
    try {
      const rules = { ...(config.point_rules || {}) };
      delete rules[kind];
      await api.patch('/admin/progression/config', { point_rules: rules, reason });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (!config) return <div className="text-sm text-neutral-500">Loading rules...</div>;
  const rules = config.point_rules || {};

  return (
    <div className="space-y-3" data-testid="admin-progression-rules">
      <div className="flex items-center justify-between">
        <div className="text-xs text-neutral-500">
          Rule version: <span className="font-mono">{config.rule_version || '-'}</span>
          <span className="ml-3">Last update: {config.updated_at?.slice(0, 19).replace('T', ' ') || '-'}</span>
        </div>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="admin-progression-rules-add">
          <Plus className="h-3 w-3 mr-1" /> Add rule
        </Button>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_auto] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 border-b bg-neutral-50">
          <div>Kind</div>
          <div className="text-right">Points</div>
          <div />
        </div>
        {Object.entries(rules).map(([kind, points]) => {
          const isFairness = FAIRNESS_DENYLIST.includes(kind);
          return (
            <div key={kind} className="grid grid-cols-[2fr_1fr_auto] gap-3 px-4 py-2.5 border-b last:border-b-0 items-center" data-testid={`admin-progression-rule-row-${kind}`}>
              <div className="text-sm">
                <span className="font-mono text-primary-900">{kind}</span>
                {isFairness && (
                  <Badge className="ml-2 bg-red-100 text-red-800 text-[10px]">fairness deny</Badge>
                )}
              </div>
              <div className="text-right text-sm font-semibold text-primary-900">+{points}</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ kind, points })} data-testid={`admin-progression-rule-edit-${kind}`}>
                  <Edit2 className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(kind)} data-testid={`admin-progression-rule-delete-${kind}`}>
                  <Trash2 className="h-3 w-3 text-red-600" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <RuleEditModal
        open={!!editing || creating}
        initial={editing}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={load}
        currentRules={rules}
      />
    </div>
  );
};

const RuleEditModal = ({ open, initial, onClose, onSaved, currentRules }) => {
  const isEdit = !!initial;
  const [kind, setKind] = useState(initial?.kind || '');
  const [points, setPoints] = useState(initial?.points ?? 10);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setKind(initial?.kind || '');
    setPoints(initial?.points ?? 10);
    setReason('');
  }, [initial, open]);

  const submit = async () => {
    if (!RULE_SLUG_RE.test(kind)) {
      alert('Slug must be lowercase, start with a letter, and be 3-64 chars ([a-z0-9_]).');
      return;
    }
    const p = parseInt(points, 10);
    if (isNaN(p) || p < 0) { alert('Points must be a non-negative integer.'); return; }
    if (FAIRNESS_DENYLIST.includes(kind)) { alert(`'${kind}' is on the fairness denylist and cannot be a point rule.`); return; }
    if (reason.trim().length < 5) { alert('Reason must be at least 5 characters (audit).'); return; }
    setBusy(true);
    try {
      const newRules = { ...currentRules, [kind]: p };
      await api.patch('/admin/progression/config', { point_rules: newRules, reason: reason.trim() });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-progression-rule-edit-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit rule' : 'Add rule'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Kind (slug)</label>
            <Input
              value={kind}
              onChange={(e) => setKind(e.target.value.toLowerCase())}
              disabled={isEdit}
              placeholder="event_checkin"
              data-testid="admin-progression-rule-modal-kind"
            />
            <div className="text-[10px] text-neutral-500 mt-1">
              Lowercase, 3-64 chars, [a-z0-9_]. Cannot change after create.
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Points</label>
            <Input
              type="number" min={0}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              data-testid="admin-progression-rule-modal-points"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Reason (audit, min 5 chars)</label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this rule is being added / changed"
              data-testid="admin-progression-rule-modal-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-progression-rule-modal-save">
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===========================================================================
// C.3 Tab 3 - Ranks + thresholds editor.
// Ranks: 10 rows, each name / subtitle / level_min / level_max / artwork_url.
// Thresholds: 30 monotonic integers, editable inline with an "insert / remove"
// note that we cannot change the count (backend rejects != 30 anyway).
// ===========================================================================
const RanksEditor = () => {
  const [config, setConfig] = useState(null);
  const [ranks, setRanks] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [reason, setReason] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/admin/progression/config');
    setConfig(data);
    setRanks(JSON.parse(JSON.stringify(data.ranks || [])));
    setThresholds([...(data.level_thresholds || [])]);
    setDirty(false); setReason('');
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateRank = (i, field, val) => {
    const next = [...ranks];
    next[i] = { ...next[i], [field]: (field === 'level_min' || field === 'level_max') ? parseInt(val, 10) || 0 : val };
    setRanks(next); setDirty(true);
  };
  const updateThreshold = (i, val) => {
    const next = [...thresholds];
    next[i] = parseInt(val, 10) || 0;
    setThresholds(next); setDirty(true);
  };

  const monotonic = useMemo(() => {
    for (let i = 1; i < thresholds.length; i++) {
      if (thresholds[i] <= thresholds[i - 1]) return false;
    }
    return thresholds.length === 30;
  }, [thresholds]);

  const save = async () => {
    if (!monotonic) { alert('Thresholds must be exactly 30 values, strictly increasing.'); return; }
    if (reason.trim().length < 5) { alert('Reason must be at least 5 characters.'); return; }
    setBusy(true);
    try {
      await api.patch('/admin/progression/config', {
        ranks, level_thresholds: thresholds, reason: reason.trim(),
      });
      await load();
      alert('Ranks + thresholds saved.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!config) return <div className="text-sm text-neutral-500">Loading ranks...</div>;

  return (
    <div className="space-y-5" data-testid="admin-progression-ranks">
      <Card>
        <CardHeader><CardTitle className="text-sm text-primary-900">10 ranks</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {ranks.map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[2fr_2fr_0.6fr_0.6fr_2fr] gap-2 items-center" data-testid={`admin-progression-rank-${r.rank_index}`}>
              <Input
                value={r.name || ''}
                onChange={(e) => updateRank(i, 'name', e.target.value)}
                placeholder="Rank name"
              />
              <Input
                value={r.subtitle || ''}
                onChange={(e) => updateRank(i, 'subtitle', e.target.value)}
                placeholder="English subtitle"
              />
              <Input
                type="number" min={1} max={30}
                value={r.level_min ?? ''}
                onChange={(e) => updateRank(i, 'level_min', e.target.value)}
                placeholder="min"
              />
              <Input
                type="number" min={1} max={30}
                value={r.level_max ?? ''}
                onChange={(e) => updateRank(i, 'level_max', e.target.value)}
                placeholder="max"
              />
              <Input
                value={r.artwork_url || ''}
                onChange={(e) => updateRank(i, 'artwork_url', e.target.value)}
                placeholder="artwork URL (optional)"
              />
            </div>
          ))}
          <div className="text-[10px] text-neutral-500">
            Changing rank names updates copy across the app. Level ranges must together cover 1..30 without gaps.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-primary-900">
            Level thresholds ({thresholds.length}/30) {!monotonic && <span className="text-red-600 ml-2 text-xs">not monotonic</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-6 gap-2">
            {thresholds.map((t, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-[10px] text-neutral-400 w-6 text-right">L{i + 1}</span>
                <Input
                  type="number" min={0}
                  value={t}
                  onChange={(e) => updateThreshold(i, e.target.value)}
                  className="text-xs"
                  data-testid={`admin-progression-threshold-${i}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-neutral-200 bg-white p-3">
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">
          Audit note (min 5 chars, required to save)
        </label>
        <Textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain the change (kept in the audit log)"
          data-testid="admin-progression-ranks-reason"
        />
        <div className="flex items-center gap-3 mt-3">
          <Button
            onClick={save}
            disabled={!dirty || busy || !monotonic || reason.trim().length < 5}
            data-testid="admin-progression-ranks-save"
          >
            {busy ? 'Saving...' : 'Save ranks + thresholds'}
          </Button>
          {dirty && (
            <button type="button" className="text-xs text-neutral-500 hover:text-neutral-800" onClick={load}>
              Discard changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// C.3 Tab 4 - Achievements CMS.
// Wraps /api/admin/achievements CRUD (F2.d.3) with a simple list + edit modal
// + manual award / revoke / retire actions.
// ===========================================================================
const AchievementsCms = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ q: '', collection: '', includeRetired: false });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.collection) params.collection = filter.collection;
    if (filter.includeRetired) params.include_retired = true;
    const { data } = await api.get('/admin/achievements', { params });
    setItems(data.rows || data.items || data.achievements || []);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const retire = async (code, retired) => {
    const url = retired
      ? `/admin/achievements/${code}/unretire`
      : `/admin/achievements/${code}/retire`;
    const reason = window.prompt(`${retired ? 'Unretire' : 'Retire'} ${code}? Reason (min 5 chars):`);
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.post(url, { reason });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const award = async (code) => {
    const uid = window.prompt(`Manual award ${code} to user id:`);
    if (!uid) return;
    const reason = window.prompt('Reason (min 5 chars):');
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.post(`/admin/achievements/${code}/award`, { user_id: uid.trim(), reason });
      alert('Awarded.');
    } catch (e) { alert(e?.response?.data?.detail || 'Award failed'); }
  };

  return (
    <div className="space-y-3" data-testid="admin-progression-achievements">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
        <Input
          value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          placeholder="Search code / name"
          data-testid="admin-progression-achievements-search"
        />
        <Input
          value={filter.collection} onChange={(e) => setFilter({ ...filter, collection: e.target.value })}
          placeholder="collection"
        />
        <label className="flex items-center gap-2 text-xs text-neutral-600">
          <input
            type="checkbox"
            checked={filter.includeRetired}
            onChange={(e) => setFilter({ ...filter, includeRetired: e.target.checked })}
            data-testid="admin-progression-achievements-retired-toggle"
          />
          Include retired
        </label>
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setCreating(true)} data-testid="admin-progression-achievements-add">
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_0.7fr_0.6fr_0.6fr_auto] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 border-b bg-neutral-50">
          <div>Code / Name</div>
          <div>Collection</div>
          <div>Rarity</div>
          <div className="text-right">Bonus</div>
          <div>Flags</div>
          <div />
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">No achievements.</div>
        ) : items.map((a) => (
          <div key={a.code} className="grid grid-cols-[2fr_1fr_0.7fr_0.6fr_0.6fr_auto] gap-3 px-4 py-2.5 border-b last:border-b-0 items-center" data-testid={`admin-progression-achievement-row-${a.code}`}>
            <div className="min-w-0">
              <div className="font-mono text-xs text-primary-900 truncate">{a.code}</div>
              <div className="text-xs text-neutral-500 truncate">{a.name}</div>
            </div>
            <div className="text-xs text-neutral-700">{a.collection || '-'}</div>
            <div className="text-xs">
              <Badge className="bg-neutral-100 text-neutral-800 text-[10px]">{a.rarity || 'common'}</Badge>
            </div>
            <div className="text-right text-sm font-semibold text-primary-900">
              {a.bonus_points || 0}
            </div>
            <div className="flex gap-1">
              {a.manual_only && <Badge className="bg-secondary-100 text-secondary-800 text-[10px]">manual</Badge>}
              {a.retired && <Badge className="bg-red-100 text-red-800 text-[10px]">retired</Badge>}
              {a.hint_visible === false && <Badge className="bg-primary-100 text-primary-900 text-[10px]">secret</Badge>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`admin-progression-achievement-edit-${a.code}`}>
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => award(a.code)} data-testid={`admin-progression-achievement-award-${a.code}`}>
                <Trophy className="h-3 w-3 text-secondary-600" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => retire(a.code, a.retired)}>
                {a.retired ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3 text-red-600" />}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AchievementEditModal
        open={!!editing || creating}
        initial={editing}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={load}
      />
    </div>
  );
};

const AchievementEditModal = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: '', name: '', description: '', collection: '',
    rarity: 'common', bonus_points: 0,
    manual_only: false, hint_visible: true,
  });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initial) setForm({
      code: initial.code, name: initial.name || '',
      description: initial.description || '',
      collection: initial.collection || '',
      rarity: initial.rarity || 'common',
      bonus_points: initial.bonus_points || 0,
      manual_only: !!initial.manual_only,
      hint_visible: initial.hint_visible !== false,
    });
    else setForm({ code: '', name: '', description: '', collection: '', rarity: 'common', bonus_points: 0, manual_only: false, hint_visible: true });
    setReason('');
  }, [initial, open]);

  const save = async () => {
    if (!isEdit && !RULE_SLUG_RE.test(form.code)) {
      alert('Code must be lowercase, start with a letter, and be 3-64 chars.');
      return;
    }
    if (!form.name || form.name.length < 3) { alert('Name is required (min 3 chars).'); return; }
    if (reason.trim().length < 5) { alert('Reason must be at least 5 characters.'); return; }
    setBusy(true);
    try {
      const payload = { ...form, bonus_points: parseInt(form.bonus_points, 10) || 0, reason: reason.trim() };
      if (isEdit) {
        await api.patch(`/admin/achievements/${form.code}`, payload);
      } else {
        await api.post('/admin/achievements', payload);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-progression-achievement-modal">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit achievement' : 'New achievement'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Code</label>
              <Input
                value={form.code}
                disabled={isEdit}
                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                data-testid="admin-progression-achievement-modal-code"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Rarity</label>
              <Select value={form.rarity} onValueChange={(v) => setForm({ ...form, rarity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['common', 'rare', 'epic', 'legendary'].map(r => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Description</label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Collection</label>
              <Input value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Bonus points</label>
              <Input type="number" min={0} value={form.bonus_points} onChange={(e) => setForm({ ...form, bonus_points: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.manual_only} onChange={(e) => setForm({ ...form, manual_only: e.target.checked })} /> Manual only
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.hint_visible} onChange={(e) => setForm({ ...form, hint_visible: e.target.checked })} /> Hint visible (not secret)
            </label>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Reason (audit)</label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-progression-achievement-modal-save">
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const AdminProgressionPage = () => {
  return (
    <div className="p-6 space-y-4" data-testid="admin-progression-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-primary-900 flex items-center gap-2">
          <Activity className="h-6 w-6 text-secondary-600" /> Progression Admin
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Manage rules, ranks, achievements. Browse user progression. Audit changes.
        </p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="users" data-testid="admin-progression-tab-users">
            <UsersIcon className="h-3.5 w-3.5 mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="admin-progression-tab-rules">
            <Sliders className="h-3.5 w-3.5 mr-1.5" /> Rules
          </TabsTrigger>
          <TabsTrigger value="ranks" data-testid="admin-progression-tab-ranks">
            <Award className="h-3.5 w-3.5 mr-1.5" /> Ranks
          </TabsTrigger>
          <TabsTrigger value="achievements" data-testid="admin-progression-tab-achievements">
            <Trophy className="h-3.5 w-3.5 mr-1.5" /> Achievements
          </TabsTrigger>
          <TabsTrigger value="ops" data-testid="admin-progression-tab-ops">
            <Activity className="h-3.5 w-3.5 mr-1.5" /> Ops
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="pt-6" data-testid="admin-progression-tab-users-panel">
          <ProgressionUsersBrowser />
        </TabsContent>
        <TabsContent value="rules" className="pt-6" data-testid="admin-progression-tab-rules-panel">
          <RulesEditor />
        </TabsContent>
        <TabsContent value="ranks" className="pt-6" data-testid="admin-progression-tab-ranks-panel">
          <RanksEditor />
        </TabsContent>
        <TabsContent value="achievements" className="pt-6" data-testid="admin-progression-tab-achievements-panel">
          <AchievementsCms />
        </TabsContent>
        <TabsContent value="ops" className="pt-6 space-y-6" data-testid="admin-progression-tab-ops-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DriftAlertsCard />
          </div>
          <AdminLedgerViewer />
          <AdminAuditFeed />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminProgressionPage;
